import * as React from 'react';

import { SHOW_ALL_ROWS_PAGE_SIZE } from './liveViewTable';

import { DataTableChip, ReactTable, Tooltip, safeParseHtml } from '../../../Shared/index';
import tooltipCopyForScoreDescriptions from '../modules/tooltipCopyForScoreDescriptions';

// A row of the teacher's activity feed (TeacherActivityFeed#hydrate)
export interface CompletedActivity {
  id: number,
  student_name: string,
  activity_name: string,
  score: string,
  completed: string,
  unit_id: number|null,
  classroom_id: number|null,
  user_id: number,
  activity_id: number,
}

const reportLink = ({ classroom_id, unit_id, user_id, activity_id, }: CompletedActivity) => (
  `/teachers/progress_reports/report_from_classroom_and_unit_and_activity_and_user/classroom/${classroom_id}/unit/${unit_id}/user/${user_id}/activity/${activity_id}`
);

const ScoreCell = ({ row }) => {
  const { score, } = row.original;
  if (!score) { return null; }

  return (
    <Tooltip
      tooltipText={tooltipCopyForScoreDescriptions[score.toLowerCase()]}
      tooltipTriggerText={<span className={`score-tag ${score.toLowerCase().split(' ').join('-')}`}>{score}</span>}
    />
  );
};

const CompletedCell = ({ row }) => {
  const activity: CompletedActivity = row.original;
  return (
    <a
      aria-label={`View report for ${activity.student_name}'s performance on ${activity.activity_name} (opens in a new tab)`}
      className="interactive-wrapper focus-on-light"
      href={reportLink(activity)}
      rel="noopener noreferrer"
      target="_blank"
    >
      {activity.completed}
    </a>
  );
};

const ActivityCell = ({ row }) => {
  const activity: CompletedActivity = row.original;
  return (
    <DataTableChip
      label={(
        <React.Fragment>
          {safeParseHtml(activity.activity_name)}
          <span className="sr-only"> – {activity.student_name}&apos;s report (opens in a new tab)</span>
        </React.Fragment>
      )}
      link={reportLink(activity)}
      openInNewTab
    />
  );
};

const columns = [
  { Header: 'Student', accessor: 'student_name', resizable: false, maxWidth: 220 },
  { Header: 'Activity', accessor: 'activity_name', resizable: false, Cell: ActivityCell },
  // Room for the longest tag, "Frequently demonstrated skill", at full size
  { Header: 'Score', accessor: 'score', resizable: false, maxWidth: 270, Cell: ScoreCell },
  { Header: 'Completed', accessor: 'completed', resizable: false, maxWidth: 130, Cell: CompletedCell },
];

interface RecentlyCompletedProps {
  activities: CompletedActivity[],
}

const RecentlyCompleted = ({ activities, }: RecentlyCompletedProps) => (
  <section aria-labelledby="recently-completed-heading" className="live-view-recently-completed">
    <h2 id="recently-completed-heading">Recently Completed Activities</h2>
    {activities.length ? (
      <div className="progress-reports-2018 live-view-table">
        <ReactTable className="progress-report" columns={columns} data={activities} defaultPageSize={SHOW_ALL_ROWS_PAGE_SIZE} disableSortBy />
      </div>
    ) : (
      <div className="live-view-empty-state">
        <p>No activities have been completed yet.</p>
        <p>Activities appear here as students finish them.</p>
      </div>
    )}
  </section>
);

export default RecentlyCompleted;
